import { Attr } from '@mode-switch/model';
import * as angular from 'angular';
import { EncField, Encoding, EncSort } from '@mode-vegas/vegas';

import { FlamingoTypes } from '@mode/shared/contract-common';

import { SchemaModel, SwitchSchemaModel } from '@mode/charts/util-vegas';
import { CompiledField, Switch } from '@mode/shared/util-switch';

export abstract class FieldController implements angular.IController {
  // bindings
  readonly encoding: Encoding;
  readonly field: EncField;
  readonly model: FlamingoTypes.ModeModel;
  readonly viewName: string;

  // set by $onChanges
  isValid: boolean;
  isNames: boolean;
  isValues: boolean;
  hasSort: boolean;
  private caretCapraIconStyle: string;
  private capraSortDirectionIcon: string;

  isSortAllowed: boolean;

  schemaModel: SchemaModel;

  error: string;
  formula: CompiledField | null;
  contextMenuOptions: Array<{ html: string; click?: (e: Event) => void }> = [];

  $onInit() {
    this.init();
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.field || changes.model || changes.encoding) {
      this.init();
    }
  }

  init() {
    if (this.field == null) {
      return; // component should be cleaning up
    }

    const view = Switch.Views.find(this.model, this.viewName);
    const compiled = CompiledField.fromEncField(view, this.field);
    this.schemaModel = SwitchSchemaModel.make(this.model, this.viewName);

    this.isNames = compiled.isNames;
    this.isValues = compiled.isValues;
    this.formula = compiled;
    this.isValid = compiled.isValid;

    if (!compiled.isValid) {
      this.error = compiled.errors.join('\n'); // TODO: format appropriately downstream
    }
    const sortField = this.getSortField();
    this.hasSort = this.isSortAllowed && sortField !== undefined;
    if (this.hasSort) {
      this.capraSortDirectionIcon = sortField.descending ? 'icon-sort-descending' : 'icon-sort-ascending';
    } else {
      this.capraSortDirectionIcon = null;
    }
    this.caretCapraIconStyle = this.getCaretCapraIconStyle();

    this.contextMenuOptions = this.buildContextMenuOptions();
  }

  getMenuItemHtml(selected: boolean, name: string, aside = '', spanClass = ''): string {
    return `
      <span class="context-item">
        <a href="#"
           tabindex="-1"
           class="context-link ${selected ? 'selected' : ''}">
          <span class="indicator" ></span>
          <span class="context-link-name ${spanClass}">${name}</span>
          <span class="context-link-aside">${aside}</span>
        </a>
      </span>
    `;
  }

  getMenuHeadingHtml(name: string, icon?: string): string {
    return `
      <span class="context-heading-item">
        ${icon ? `<img class="icon" src="${icon}" />` : ''}
        <span class="context-heading">
          ${name}
        </span>
      </span>
    `;
  }

  abstract buildContextMenuOptions(): Array<{ html: string; click?: (e: Event) => void }>;

  getSortField(): Readonly<EncSort> | undefined {
    if (this.encoding) {
      return this.encoding.sorts.filter((s) => s.source === this.field.formula)[0];
    }
  }

  get isContinuous(): boolean {
    if (this.formula == null) {
      return false;
    } else {
      return this.formula.varType === Attr.Type.Continuous;
    }
  }

  get isDiscrete(): boolean {
    if (this.formula == null) {
      return false;
    } else {
      return this.formula.varType === Attr.Type.Discrete && !this.formula.isNames;
    }
  }

  get displayName(): string {
    return this.formula && this.formula.displayFormula;
  }

  getCaretCapraIconStyle(): string {
    if (!this.isValid) {
      return 'invalid';
    }

    if (this.isDiscrete) {
      return 'discrete';
    }

    if (this.isContinuous) {
      return 'continuous';
    }
    if (this.isNames && this.hasSort) {
      return 'is-names';
    }
    return 'hidden';
  }
}

export const FieldComponent: angular.IComponentOptions = {
  bindings: {
    encoding: '<',
    field: '<',
    model: '<',
    viewName: '<',
    onDragStart: '&',
    onDragCancel: '&',
  },
  controllerAs: 'ctrl',
  template: `
    <div class="enc-field-wrapper"
      ng-dblclick="ctrl.handleDoubleClick && ctrl.handleDoubleClick()"
      draggable="{{!ctrl.isNames}}"
      ng-dragstart="ctrl.onDragStart({ $event: $event, $field: ctrl.formula, $valid: ctrl.isValid })"
      ng-dragend="ctrl.onDragCancel({ $event: $event, $field: ctrl.field })">
        <div ng-class="{discrete: ctrl.isDiscrete, continuous: ctrl.isContinuous, invalid: !ctrl.isValid}"
            class="enc-field"
            title="{{ !ctrl.isValid ? ctrl.error : ctrl.displayName }}"
            context-menu="ctrl.contextMenuOptions"
            context-menu-class="context-menu-ng context-menu-quick-charts">
          <span class="text" ng-class="{'system-value': ctrl.isNames || ctrl.isValues}">{{ ctrl.displayName }}</span>
          <capra-icon
            ng-if="ctrl.hasSort"
            icon="{{ ctrl.capraSortDirectionIcon }}"
            class="sort-icon"
            ng-class="ctrl.caretCapraIconStyle">
          </capra-icon>
          <capra-tooltip
            ng-if="!ctrl.isValid"
            [tooltip-content]="ctrl.error"
            tooltip-placement="bottom"
            tooltip-delay="100"
            tooltip-interactive="false"
            tooltip-size="regular-thin">
              <capra-icon
                icon="icon-warning"
                class="warning-state">
              </capra-icon>
          </capra-tooltip>
          <span class="context-menu-target"
            context-menu="ctrl.contextMenuOptions"
            context-menu-class="context-menu-ng context-menu-quick-charts"
            context-menu-on="click">
            <capra-icon icon="icon-caret-down" ng-class="ctrl.caretCapraIconStyle"></capra-icon>
          </span>
        </div>
    </div>
  `,
};
